/**
 * AuthContext — JWT-based auth state management.
 *
 * Stores the access token in localStorage and exposes `user`, `login`,
 * `register`, `logout`, `refresh` helpers. Also installs an axios
 * interceptor (via the shared instance in @/lib/api) so every request
 * automatically carries the Bearer token.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api, apiGet, apiPost, apiPatch } from "@/lib/api";

const TOKEN_KEY = "cse_auth_token";

const AuthContext = createContext(null);

const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

const writeToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set to `true` right after a successful fresh login. The app-wide
  // DisclaimerGate reads this to decide whether to prompt for disclaimer
  // acceptance. Existing mid-session users (page refresh / new tab on an
  // old token) intentionally do NOT trigger the prompt — they get a
  // grace period until their next fresh login, per the product spec.
  const [pendingDisclaimerCheck, setPendingDisclaimerCheck] = useState(false);

  // Install request interceptor once
  useEffect(() => {
    const reqId = api.interceptors.request.use((config) => {
      const t = readToken();
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    const resId = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          writeToken(null);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, []);

  const fetchMe = useCallback(async () => {
    if (!readToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await apiGet("/auth/me");
      setUser(me);
    } catch {
      writeToken(null);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await apiPost("/auth/login", { email, password });
      writeToken(res.access_token);
      setToken(res.access_token);
      setUser(res.user);
      // Mark this as a "fresh login" so the DisclaimerGate can prompt
      // if the user's accepted_version doesn't match the active one.
      setPendingDisclaimerCheck(true);
      return res.user;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Login failed";
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError(null);
    try {
      return await apiPost("/auth/register", payload);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Registration failed";
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(() => {
    writeToken(null);
    setToken(null);
    setUser(null);
    // Clear per-session banner dismissals so the user sees them again on next login
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith("cse_banner_dismissed_")) keys.push(k);
      }
      keys.forEach((k) => sessionStorage.removeItem(k));
    } catch (e) { /* ignore */ }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const updated = await apiPatch("/auth/me", patch);
    setUser(updated);
    return updated;
  }, []);

  const startTrial = useCallback(async () => {
    const updated = await apiPost("/auth/me/start-trial");
    setUser((prev) => ({ ...(prev || {}), ...updated }));
    return updated;
  }, []);

  const value = useMemo(
    () => {
      // Centralised admin detection. Any one of these signals is enough:
      //   1. user.role === "admin"        (canonical, set on the user doc)
      //   2. user.is_admin === true       (legacy / belt-and-braces fallback)
      // This MUST be the single source of truth for hiding trial chips,
      // subscription banners and tier badges across the UI.
      const isAdmin = !!user && (
        user.role === "admin" ||
        user.is_admin === true
      );
      // BETA MODE — every authenticated user is treated as Premium for
      // feature gating. The only thing still gated by admin is RUNNING
      // new Deep Analyses (cost control); checked separately via
      // `feature_flags.limits.deep_analysis_can_run`.
      return {
        user,
        token,
        loading,
        error,
        isAuthenticated: !!user,
        isAdmin,
        isPremium: !!user,
        login,
        register,
        logout,
        refresh: fetchMe,
        updateProfile,
        startTrial,
        // Disclaimer gate wiring
        pendingDisclaimerCheck,
        clearDisclaimerCheck: () => setPendingDisclaimerCheck(false),
      };
    },
    [user, token, loading, error, login, register, logout, fetchMe, updateProfile, startTrial, pendingDisclaimerCheck]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

// Re-export for convenience: shows unauthenticated users a redirect / placeholder
export { TOKEN_KEY };
